import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { inboxActionsApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { offlineSessionIdentity } from '../state/offline-cache.ts';
import { ActionCard } from '../components/inbox-action-suggestions.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import { InboxKindApprovalSwitch } from './inbox-kind-approval.tsx';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import '../components/messages-presentation.css';

export default function InboxApprovalPage() {
  const { t } = useTranslation();
  return (
    <div className="messages-page messages-approval">
      <WorkPage title={t('inboxAction.confirm')} description={t('inboxAction.confirmReview')}>
        <nav aria-label={t('nav.messages')}>
          <Link to="/messages" className="text-[color:var(--color-brand-strong)] underline underline-offset-4">
            {t('nav.messages')}
          </Link>
        </nav>
        <InboxKindApprovalSwitch framed>
          <LegacyInboxApprovalPage />
        </InboxKindApprovalSwitch>
      </WorkPage>
    </div>
  );
}

function LegacyInboxApprovalPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const session = useSession();
  const identity = JSON.stringify([
    offlineSessionIdentity(),
    session.user?.id,
    session.user?.role,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.permissions,
    session.company?.accessLocked,
    session.company?.inboxActionsAvailable,
    session.company?.planFeatures,
    session.impersonatedBy,
  ]);
  const scope = useRef({ identity, id, token, epoch: 0 });
  if (scope.current.identity !== identity || scope.current.id !== id || scope.current.token !== token)
    scope.current = { identity, id, token, epoch: scope.current.epoch + 1 };
  const captured = scope.current;
  const scopedIdentity = `${identity}:${captured.epoch}`;
  if (
    !session.company?.inboxActionsAvailable ||
    session.user?.role === 'crew' ||
    session.company.accessLocked ||
    !session.can('customers:read') ||
    !session.can('schedule:read')
  ) {
    return <Alert tone="error">{t('inboxAction.linkError')}</Alert>;
  }
  return (
    <Approval
      key={`${id}:${scopedIdentity}`}
      id={id}
      token={token}
      identity={scopedIdentity}
      scopeCurrent={() => scope.current === captured && offlineSessionIdentity() === JSON.parse(identity)[0]}
      canDecide={session.can('customers:write') && session.can('schedule:write') && !session.company?.accessLocked}
    />
  );
}

function Approval({
  id,
  token,
  identity,
  canDecide,
  scopeCurrent,
}: {
  id: string;
  token: string;
  identity: string;
  canDecide: boolean;
  scopeCurrent: () => boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const key = ['inbox-approval', id, token, identity];
  const proposal = useQuery({ queryKey: key, queryFn: () => inboxActionsApi.confirm(id, token), retry: false });
  const preview = useRef({ data: proposal.data, denied: Boolean(proposal.error) });
  if (preview.current.data !== proposal.data || preview.current.denied !== Boolean(proposal.error))
    preview.current = { data: proposal.data, denied: Boolean(proposal.error) };
  const capturedPreview = preview.current;
  const previewCurrent = () => {
    const state = client.getQueryState(key);
    return Boolean(
      preview.current === capturedPreview && proposal.data && state?.data === proposal.data && !state.error,
    );
  };
  const decision = useMutation({
    mutationFn: (value: 'approve' | 'dismiss') => {
      if (!active.current || !scopeCurrent() || !previewCurrent()) throw new Error('stale');
      return inboxActionsApi.decide(id, value, token);
    },
    onSuccess: () => (active.current && scopeCurrent() ? client.invalidateQueries({ queryKey: key }) : undefined),
    onError: () => {
      if (active.current && scopeCurrent()) {
        preview.current = { ...preview.current };
        return client.invalidateQueries({ queryKey: key });
      }
    },
  });
  return (
    <WorkSection title={t('messagesInbox.reviewSection')}>
      {proposal.isPending ? (
        <LoadingBlock label={t('common.loading')} />
      ) : proposal.error ? (
        <Alert tone="error">
          {t('inboxAction.linkError')}
          <Button variant="secondary" onClick={() => void proposal.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : proposal.data ? (
        <ActionCard
          suggestion={proposal.data.suggestion}
          canDecide={canDecide && !decision.isPending && !decision.error}
          onDecide={(value) => {
            if (active.current && scopeCurrent() && previewCurrent()) decision.mutate(value);
          }}
        />
      ) : null}
      {decision.error ? (
        <Alert tone="error">
          {t('inboxAction.stale')}
          <Button
            variant="secondary"
            disabled={proposal.isFetching}
            onClick={async () => {
              const result = await proposal.refetch();
              if (active.current && scopeCurrent() && !result.error && result.data) decision.reset();
            }}
          >
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
    </WorkSection>
  );
}
